import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import { Menu, X, LogOut, User } from 'lucide-react';
import type { UserRole } from '../../types';

interface NavigationItem {
  name: string;
  href: string;
  roles: UserRole[];
  icon?: React.ReactNode;
}

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getNavigationLinks = (): NavigationItem[] => {
    if (!user) return [];

    switch (user.role) {
      case 'admin':
        const adminLinks: NavigationItem[] = [
          { name: 'Dashboard', href: '/admin', roles: ['admin' as UserRole] },
          { name: 'Organizations', href: '/admin/organizations', roles: ['admin' as UserRole] },
          { name: 'System Settings', href: '/admin/settings', roles: ['admin' as UserRole] },
        ];
        // Add Super Admin link if user has is_admin flag
        if (user?.is_admin) {
          adminLinks.push({ name: 'Super Admin', href: '/super-admin', roles: ['admin' as UserRole] });
        }
        return adminLinks;
      case 'orgadmin':
        return [
          { name: 'Dashboard', href: '/org-admin', roles: ['orgadmin'] },
          { name: 'Users', href: '/org-admin/users', roles: ['orgadmin'] },
          { name: 'Tasks', href: '/org-admin/tasks', roles: ['orgadmin'] },
          { name: 'Settings', href: '/org-admin/settings', roles: ['orgadmin'] },
          { name: 'Billing', href: '/account/billing', roles: ['orgadmin'] },
        ];
      case 'interviewer':
        return [
          { name: 'Dashboard', href: '/interviewer', roles: ['interviewer'] },
          { name: 'Candidates', href: '/candidates', roles: ['interviewer'] },
          { name: 'Templates', href: '/templates', roles: ['interviewer'] },
        ];
      case 'candidate':
        return [
          { name: 'Dashboard', href: '/dashboard', roles: ['candidate'] },
          { name: 'Tasks', href: '/dashboard/tasks', roles: ['candidate'] },
          { name: 'Profile', href: '/profile', roles: ['candidate'] },
        ];
      default:
        return [];
    }
  };

  const navigation = getNavigationLinks();
  const filteredNavigation = navigation.filter(item => user?.role && item.roles.includes(user.role as UserRole));

  const isActivePath = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="flex min-h-screen bg-neutral-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-white border-r border-neutral-200 shadow-sm">
        {/* Logo */}
        <div className="h-16 flex-shrink-0 flex items-center justify-center border-b border-neutral-200">
          <Link to="/" className="text-xl font-bold text-primary-600 hover:text-primary-700 transition-colors">
            SkillFoundry
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNavigation.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`
                nav-link
                ${isActivePath(link.href) ? 'nav-link-active' : ''}
              `}
            >
              {link.icon && (
                <span className="mr-3 h-5 w-5">
                  {link.icon}
                </span>
              )}
              {link.name}
            </Link>
          ))}
        </nav>
        
        {/* User Section */}
        {user && (
          <div className="p-4 border-t border-neutral-200 bg-neutral-50">
            <div className="flex items-center mb-3">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt="User" 
                  className="h-8 w-8 rounded-full object-cover" 
                />
              ) : (
                <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600" />
                </div>
              )}
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user.name || user.email}
                </p>
                <p className="text-xs text-neutral-500 truncate capitalize">
                  {user.role}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full btn-secondary text-sm"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        )}
      </aside>

      {/* Mobile Layout */}
      <div className="flex flex-col flex-1 w-0">
        {/* Mobile Header */}
        <header className="lg:hidden h-16 flex items-center justify-between px-4 bg-white border-b border-neutral-200 shadow-sm">
          <Link to="/" className="text-xl font-bold text-primary-600">
            SkillFoundry
          </Link>
          
          <div className="flex items-center space-x-3">
            {user && (
              <button
                onClick={handleLogout}
                className="btn-secondary btn-sm"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors"
              aria-label="Open navigation menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </header>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-neutral-200 shadow-sm">
            <nav className="px-4 py-4 space-y-1">
              {filteredNavigation.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    nav-link
                    ${isActivePath(link.href) ? 'nav-link-active' : ''}
                  `}
                >
                  {link.icon && (
                    <span className="mr-3 h-5 w-5">
                      {link.icon}
                    </span>
                  )}
                  {link.name}
                </Link>
              ))}
            </nav>
            
            {user && (
              <div className="px-4 py-3 border-t border-neutral-200 bg-neutral-50">
                <div className="flex items-center text-sm">
                  <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-neutral-900">{user.name || user.email}</p>
                    <p className="text-neutral-500">{user.email}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 container-padding section-padding overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;